import {
  ConstructGeneralViewStateParam,
  ProductsLeaderboardTabsProps,
  ProductsOverviewTabProps,
  ProductsTrendsTabProps,
  ProductsViewState,
  ProductTabRestoreLocalSavedViewProps,
} from '@models/SavedViews';
import { useDispatch, useSelector } from 'react-redux';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { bindActionCreators } from 'redux';
import { actionCreators, actionCreatorsSideFilters } from '@state/Index';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useRestoreSavedView } from '@ecosystem/newron-client-lib';
import {
  checkIsSubscribed,
  constructGeneralViewStateProps,
  getGrowthType,
  isMultibannerChanged,
  restoreGeneralViewState,
} from './SavedViewHookUtils';
import { ProductsTabView } from '@components/tab-nav/TabConfig';
import { useRestoreMultibannerRetailerClient } from './UseRestoreMultibannerRetailerClient';
import { useRestoreProductMultibannerRetailerClient } from './UseRestoreProductMultibannerRetailerClient';
import {
  restoreProductsLeaderboardTabViewState,
  restoreProductsOverviewTabViewState,
  restoreProductsTrendsTabViewState,
} from './SavedViewProductHookUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

/**
 *  React custom hooks to restore the saved views for Performance Analysis page
 *  @param productsRestoreLocalSavedViewProps contains local setState from PA page
 */
export const useRestoreProductsSavedView = (
  productsRestoreLocalSavedViewProps: ProductTabRestoreLocalSavedViewProps,
): void => {
  const {
    productType,
    setIsSubscribed,
    setOverviewShareVsValueVolumeFilterType,
    setTrendsSelectedKpi,
    setTrendsSelectedFocusArea,
    setTrendsSelectedSaleType,
    setTrendsSetSelectedLineChartSaleType,
    setTrendsSelectedRetailerOption,
    setLeaderboardIsTableView,
    setLeaderboardSelectedDisplayType,
    setLeaderboardSelectedSaleType,
    setLeaderboardSelectedSortingOption,
    setShowPgModal,
    selectedTab,
    selectedLink,
    setRelativePeriod,
    setTimeFrame,
    setPeriod,
    setFilters,
    setAppliedCountries,
    setSelectedCountries,
    setDefaultFilters,
    setGrowthOption,
  } = productsRestoreLocalSavedViewProps;

  // generate object holding the general states to be passed in the function later on
  const generalSavedViewProps = constructGeneralViewStateProps(productsRestoreLocalSavedViewProps);
  const dispatch = useDispatch();
  const pagesConfig = usePageConfig();
  const { restoreMultibannerRetailerClient } = useRestoreMultibannerRetailerClient();
  const { restoreProductMultibannerRetailerClient } = useRestoreProductMultibannerRetailerClient();

  const retailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);
  const mainFiltersState = useSelector((state: any) => state.mainFilters);

  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);

  const pageName = PageName.RI_PRODUCT;
  const pageType = PageType.RI_PRODUCT;

  const applyProductsViewState = (viewState: ProductsViewState): void => {
    // initial checks
    if (!checkIsSubscribed(viewState.retailerClientState, pagesConfig, productType)) {
      setIsSubscribed(false);
      return;
    }

    if (!viewState) {
      return;
    }

    const {
      appliedFilters,
      overviewShareVsValueVolumeFilterType,
      trendsSelectedKpi,
      trendsSelectedFocusArea,
      trendsSelectedSaleType,
      trendsSetSelectedLineChartSaleType,
      trendsSelectedRetailerOption,
      leaderboardIsTableView,
      leaderboardSelectedDisplayType,
      leaderboardSelectedSaleType,
      leaderboardSelectedSortingOption,
      sorting,
      hitlistRetailerClientState,
      hitlistInitialFilterState,
      productMainFilterState,
      appliedCountries,
      initialFilterState,
      growthType,
    } = viewState;

    const params: ConstructGeneralViewStateParam = {
      viewState,
      pagesConfig,
      pageType,
      pageName,
      updateSideFilters,
      updateMainFilters,
      mainFiltersState,
      generalSavedViewProps,
    };

    // restore the general states that exist in all products pages
    restoreGeneralViewState(params);

    if (appliedFilters) {
      setShowPgModal(false);
    }

    if (viewState.productMainFilterState) {
      setRelativePeriod(viewState.productMainFilterState.comparisonPeriod);
      setTimeFrame(viewState.productMainFilterState.timeFrame as Periodicity);
      setPeriod(viewState.productMainFilterState.period);
    }

    switch (selectedTab?.tabValues[0] ?? selectedLink?.value) {
      case ProductsTabView.Overview: {
        const productsOverviewTabProps: ProductsOverviewTabProps = {
          overviewShareVsValueVolumeFilterType,
          setOverviewShareVsValueVolumeFilterType,
        };
        restoreProductsOverviewTabViewState(productsOverviewTabProps);
        break;
      }
      case ProductsTabView.Trends: {
        const productsTrendsTabProps: ProductsTrendsTabProps = {
          trendsSelectedKpi,
          setTrendsSelectedKpi,
          trendsSelectedFocusArea,
          setTrendsSelectedFocusArea,
          trendsSelectedSaleType,
          setTrendsSelectedSaleType,
          trendsSetSelectedLineChartSaleType,
          setTrendsSetSelectedLineChartSaleType,
          trendsSelectedRetailerOption,
          setTrendsSelectedRetailerOption,
        };
        restoreProductsTrendsTabViewState(productsTrendsTabProps);
        break;
      }
      case ProductsTabView.Leaderboard: {
        const productsTrendsTabProps: ProductsLeaderboardTabsProps = {
          leaderboardIsTableView,
          setLeaderboardIsTableView,
          leaderboardSelectedDisplayType,
          setLeaderboardSelectedDisplayType,
          leaderboardSelectedSaleType,
          setLeaderboardSelectedSaleType,
          leaderboardSelectedSortingOption,
          setLeaderboardSelectedSortingOption,
          sorting,
          setSortingOptionsProducts: pagesConfig.setSortingOptionsProducts,
          growthOption: getGrowthType(growthType),
          setGrowthOption,
        };
        restoreProductsLeaderboardTabViewState(productsTrendsTabProps);
        break;
      }
      default:
        break;
    }

    // Props to restore product page when multibanner changed
    const restoreProductMultibannerProps = {
      pagesConfig: pagesConfig,
      hitlistRetailerClientState: hitlistRetailerClientState,
      hitllistInitialFilterState: hitlistInitialFilterState,
      productMainFilterState: productMainFilterState,
      appliedCountry: appliedCountries,
      pageName: pageName,
    };

    //Props to restore PA page when multibanner changed
    const restoreMultibannerProps = {
      pagesConfig: pagesConfig,
      retailerClientState: retailerClientState,
      initialFilterState: initialFilterState,
      appliedCountry: appliedCountries,
      pageName,
      pageType,
      setFilters,
      setAppliedCountries,
      setSelectedCountries,
      setDefaultFilters,
    };

    //Check If Multibanner Retailer is Changed
    isMultibannerChanged(
      hitlistRetailerClientState.clientId,
      retailerClientState.clientId,
      restoreMultibannerProps,
      restoreProductMultibannerProps,
      restoreMultibannerRetailerClient,
      restoreProductMultibannerRetailerClient,
    );
  };
  useRestoreSavedView(applyProductsViewState);
};
